<template>
  <div v-show="show_marker" @click="makeActive" @keyup.enter='makeActive' :id="'q_'+interaction.id" class="sm-pad marker text-xs-center "
    :class="[active ? 'active_q' : '', liveSessionMarker ? 'disabledMarker' : '']" :style="active_highlight"
    style='position:relative' role='button' :aria-label='aria_label' tabindex='0'>
    <span class='timeline-marker' data-position="right" data-delay="50" tabindex='-1'>
       <v-icon v-if="isLiveSession && interaction.interactionText.includes('app.sli.do')" size="18">$vuetify.icons.slidoIcon</v-icon>
       <v-icon
        v-else-if="isLiveSession && interaction.interactionType === 'pause_q' && interaction.interactionText.includes('Table of Contents')"
        color="white">bookmark_border</v-icon>
       <v-icon v-else color="white">{{icon}}</v-icon>
    </span>
  </div>
</template>

<script>
import Color from 'color'
import _ from 'lodash';
import moment from 'moment';
import { mapState, mapGetters, mapActions } from 'vuex';

export default {
  name: 'timeline-marker',
  components: {},
  props: [
    'interactionId',
    'scrollbar',
  ],
  data() {
    return {
      liveSessionInteraction: false,
    };
  },
  computed: {
    ...mapState('player', [
      'isLiveSession',
      'liveSessionUnixTime',
  		'liveSessionTimeArray',
    ]),
    ...mapGetters('player', [
      'branding',
      'bulbInitialized',
      'displayTime',
      'user',
      'maxTime',
      'minSidebarPx',
      'requiredMaxTime',
      'sidebarOpen',
      'showInteractionsTab',
      // --- BROADCAST CODE STARTS
  		'setBroadcastId',
  		'isBroadcast',
      // -- BROADCAST CODE ENDS
    ]),
    ...mapGetters('interactions', {
      'activeInteraction': 'activeInteraction',
      'activeInteractionMeta': 'activeInteractionMeta',
      'icons': 'icons',
      'interactions': 'interactions',
      'interactionMetas': 'interactionMetas',
      'interactionStates': 'interactionStates',
      'types': 'types',
      allConcurrentInteractions: 'concurrentInteractions',
    }),
    // vuex
    interaction() {
      return this.$store.getters['interactions/interaction'](this.interactionId);
    },
    interactionAttempt() {
      return this.$store.getters['interactions/interactionAttempt'](this.interactionId) || {};
    },
    interactionMeta() {
      return this.$store.getters['interactions/interactionMeta'](this.interactionId) || {};
    },
    active() {
      /**
       * Boolean: if this interaction is the current visible and active interaction
       */
      return this.activeInteraction ? this.interactionId === this.activeInteraction.id : false;
    },
    active_highlight() {
      if (this.interactionMeta.inDisplayRange && this.showInteractionsTab) {
        if (this.branding.activeColor === '#2196F3') return {backgroundColor: '#0D47A1'};
        const hex = this.branding.baseColor || Color(this.branding.activeColor).darken(.4).hex()
        return {backgroundColor: hex}
      }
      return '' 
    },
    aria_label() {
      return `${this.types[this.interaction.interactionType]} interaction. Click on this button to jump to ${this.convertSecondsToTimestamp(this.interaction.displayInTime)}.`
    },
    icon() {
      if (this.interaction.isRequired && !this.interactionMeta.isInteractionAnswered) return 'lock';
      return this.icons[this.interaction.interactionType];
    },
    liveSessionInteractionInDisplayRange() {
      if (this.liveSessionInteraction) {
        /*
        * If the interaction is in the unixDisplayTime range or is a durational
        * interaction inside the playerDisplayTime range we allow it to be clicked.
        * This allows durational interactions to be clicked on when overlapped by
        * single point interactions
        * [ ] - can remove specific exceptions for slido and ToC (as both are durational)
        */
        return (
          (
            _.get(this.liveSessionInteraction, 'unixDisplayInTime', 0) <= this.liveSessionUnixTime
            && _.get(this.liveSessionInteraction, 'unixDisplayOutTime', 0) >= this.liveSessionUnixTime
          )
          || (
            _.get(this.liveSessionInteraction, 'playerDisplayInTime') <= this.displayTime
            && _.get(this.liveSessionInteraction, 'playerDisplayOutTime') >= this.displayTime
            && _.get(this.liveSessionInteraction, 'playerDisplayInTime') !== _.get(this.liveSessionInteraction, 'playerDisplayOutTime')
          )
        );
      }
      return false;
    },
    liveSessionMarker() {
      if (
        this.isLiveSession
        && (
          !this.interaction.interactionText.includes('app.sli.do')
          && !this.interaction.interactionText.includes('Table of Contents')
        )
        && !this.active
        && !this.liveSessionInteractionInDisplayRange
      ) return true;
      return false;
    },
    show_marker() {
      return this.interactionMeta.inDisplayRange && !this.interactionMeta.overlay && !this.interactionMeta.hiddenByRequired;
    },
  },
  methods: {
    ...mapActions('interactions', [
      'requestSetActiveInteraction',
    ]),
    getTooltipColor() {
      if (!this.interactionMeta) return 'grey';
      if (!this.interactionMeta.isInteractionAnswered || !_.isNumber(this.interaction.pointsAvailable)) return 'grey';
      if (this.interactionMeta.isInteractionAutograded) {
        return (this.interactionMeta.isAnswerCorrect) ? 'green' : 'red';
      } else {
        return 'grey';
      }
    },
    getTooltipIcon() {
      if (!this.interactionMeta) return 'remove';
      if (!this.interactionMeta.isInteractionAnswered) return 'remove';
      if (this.interactionMeta.isInteractionAutograded) {
        return (this.interactionMeta.isAnswerCorrect) ? 'check_circle' : 'cancel';
      } else {
        return 'check_circle';
      }
    },
    makeActive() {
      if(!this.isBroadcast || (this.isBroadcast && this.user.userType === 'teacher') || (this.isBroadcast && this.user.userType === 'student' && this.interactionId === this.activeInteraction.id)) {
        this.requestSetActiveInteraction(this.interaction)
      }
    },
    convertSecondsToTimestamp(seconds) {
      return moment.utc(seconds*1000).format('HH:mm:ss');
    },
  },
  mounted() {
    if (this.isLiveSession) {
      this.liveSessionInteraction = _.find(this.liveSessionTimeArray, { 'interactionId': this.interactionId });
    }
  },
  watch: {
    'active'() {
      if (this.active) {
        // this marker is now active, scroll to that scrollbar
        this.scrollbar.scrollToElement(this.$el, 1500, null, true);
      }
    },
    'show_marker'() {
      setTimeout(() => {
        this.scrollbar.refresh();
        this.scrollbar.scrollToElement(this.$el, 1500, null, true);
      }, 0);
    }
  },
};
</script>

<style>
  .marker::after{
    left: 42px;
    content: '';
    position: absolute;
    transition: transform .3s;
    transform: translateY(5px);
    z-index: 1;
  }
  .marker.active_q::after{
    border-top: solid 12px transparent !important;
    border-right: solid 15px #FFF !important;
    border-bottom: solid 12px transparent !important;
    transform: translateY(5px);
  }
  .marker.playhead::after{
    border-top: solid 12px transparent !important;
    border-right: solid 15px #FFF !important;
    border-bottom: solid 12px transparent !important;
    transform: translateY(33px);
  }

  .before-playhead::after { transform: translateY(33px)}
  .after-playhead::after { transform: translateY(0px)}


  .timeline-marker.btn-floating{
    width:35px;
    height:35px;
  }
  .timeline-marker i{
    padding:0;
    font-size:1.25em !important;
    line-height:35px !important;
  }

  .timeline-marker.active {
    /*border:solid 3px purple;*/
  }
  .timeline-marker.pulse{
    overflow: visible;
  }
  .timeline-marker-tooltip {
    display: flex;
    align-items: center;
    justify-content: flex-start;
  }
  .tooltip__content {
    background: black;
  }
  .timeline-marker-tooltip i {
    margin-left: 5px;
  }
  .disabledMarker {
    pointer-events: none;
  }
</style>
